import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { setUserSession } from "./service/AuthService";
import config from "./config";

const APIUrl = config.API_BASE_URL + "/login";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg:     "#08101e",
  bg1:    "#0c1525",
  bg2:    "#101b2e",
  border: "#1e2d42",
  border2:"#243548",
  text:   "#e8edf5",
  body:   "#b8c8dc",
  meta:   "#6a7d95",
  green:  "#00e896",
  cyan:   "#00c8ff",
  red:    "#ff4466",
};
const FONT = "'DM Mono', monospace";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');
  * { box-sizing: border-box; }
  .login-input {
    width: 100%;
    background: ${C.bg};
    border: 1px solid ${C.border};
    color: ${C.text};
    font-family: ${FONT};
    font-size: 12px;
    padding: 10px 14px;
    border-radius: 4px;
    outline: none;
    transition: border-color .15s;
    letter-spacing: .3px;
  }
  .login-input:focus { border-color: ${C.cyan}; }
  .login-input::placeholder { color: ${C.meta}; }
  .login-input:disabled { opacity: 0.5; cursor: not-allowed; }
  .login-btn {
    width: 100%;
    padding: 11px;
    background: ${C.cyan}15;
    border: 1px solid ${C.cyan}40;
    color: ${C.cyan};
    font-family: ${FONT};
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 1.2px;
    border-radius: 4px;
    cursor: pointer;
    transition: background .15s, border-color .15s;
  }
  .login-btn:hover:not(:disabled) {
    background: ${C.cyan}25;
    border-color: ${C.cyan}70;
  }
  .login-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  @keyframes orbPulse {
    0%,100% { transform: scale(1); opacity: .15; }
    50%      { transform: scale(3); opacity: 0; }
  }
`;

const Orb = ({ color, size = 8, speed = "3s" }) => (
<div style={{ position:"relative", width:size, height:size, flexShrink:0 }}>
<div style={{ position:"absolute", inset:0, borderRadius:"50%", background:color, animation:`orbPulse ${speed} ease-in-out infinite` }} />
<div style={{ position:"absolute", inset:"25%", borderRadius:"50%", background:color }} />
</div>
);

const Login = (props) => {
  const [username,     setUsername]     = useState("");
  const [password,     setPassword]     = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading,      setLoading]      = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (username.trim() === "" || password.trim() === "") {
      setErrorMessage("Username and password are required");
      return;
    }
    setErrorMessage(null);
    setLoading(true);
    axios.post(APIUrl, { username, password }, { headers: { "x-api-key": config.API_KEY } })
        .then((response) => {
          const { user, token } = response.data;
          const role = user.role || "admin";
          setUserSession(user, token, role);
          props.history.push(role === "follower" ? "/myTrades" : "/trading");
        })
        .catch((error) => {
          setLoading(false);
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            setErrorMessage("Invalid username or password");
          } else {
            setErrorMessage("Server error — please try again later");
          }
        });
  };

  return (
      <div style={{
    background:     C.bg,
        minHeight:      "100vh",
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        padding:        "20px",
        fontFamily:     FONT,
  }}>
  <style>{CSS}</style>

  <div style={{ width:"100%", maxWidth:360 }}>

  {/* ── Brand mark ─────────────────────────────────────────────────── */}
  <div style={{ textAlign:"center", marginBottom:32 }}>
  <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}>
  <Orb color={C.cyan} size={10} speed="2.5s" />
      </div>
      <div style={{ fontFamily:FONT, fontWeight:500, fontSize:14, color:C.text, letterSpacing:"2px", marginBottom:6 }}>
  BERBUL.AI
  </div>
  <div style={{ fontFamily:FONT, fontSize:9, color:C.meta, letterSpacing:"1px" }}>
  AUTONOMOUS TRADING · SPY 0DTE
  </div>
  </div>

  {/* ── Card ───────────────────────────────────────────────────────── */}
  <div style={{
    background:   C.bg1,
        border:       `1px solid ${C.border}`,
        borderRadius: 12,
        padding:      "32px 32px 28px",
  }}>
  {/* Card header */}
  <div style={{ marginBottom:24 }}>
  <div style={{ fontFamily:FONT, fontSize:9, color:C.meta, letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:6 }}>
  Sign in
  </div>
  <div style={{ fontFamily:FONT, fontSize:11, color:C.body, fontWeight:300 }}>
  Enter your credentials to access the platform
  </div>
  </div>

  {/* Username */}
  <div style={{ marginBottom:16 }}>
  <label style={{ display:"block", fontFamily:FONT, fontSize:9, color:C.meta, letterSpacing:"1px", textTransform:"uppercase", marginBottom:6 }}>
  Username
  </label>
  <input
  className="login-input"
  type="text"
  value={username}
  onChange={e => setUsername(e.target.value)}
  disabled={loading}
  placeholder="enter username"
  autoComplete="username"
      />
      </div>

      {/* Password */}
      <div style={{ marginBottom:8 }}>
  <label style={{ display:"block", fontFamily:FONT, fontSize:9, color:C.meta, letterSpacing:"1px", textTransform:"uppercase", marginBottom:6 }}>
  Password
  </label>
  <input
  className="login-input"
  type="password"
  value={password}
  onChange={e => setPassword(e.target.value)}
  disabled={loading}
  placeholder="enter password"
  autoComplete="current-password"
  onKeyDown={e => e.key === "Enter" && handleSubmit(e)}
  />
  </div>

  {/* Forgot password link — sits below password field */}
  <div style={{ marginBottom:24, textAlign:"right" }}>
  <Link
  to="/forgot-password"
  style={{ fontFamily:FONT, fontSize:9, color:C.meta, textDecoration:"none", letterSpacing:".3px" }}
  >
  Forgot password?
  </Link>
  </div>

  {/* Error */}
  {errorMessage && (
  <div style={{
    background:   C.red + "12",
        border:       `1px solid ${C.red}30`,
        borderRadius: 4,
        padding:      "9px 12px",
        marginBottom: 16,
        display:      "flex",
        alignItems:   "center",
        gap:          8,
  }}>
  <div style={{ width:5, height:5, borderRadius:"50%", background:C.red, flexShrink:0 }} />
  <span style={{ fontFamily:FONT, fontSize:10, color:C.red }}>{errorMessage}</span>
  </div>
  )}

  {/* Submit */}
  <button
  className="login-btn"
  onClick={handleSubmit}
  disabled={loading}
      >
      {loading ? "SIGNING IN..." : "SIGN IN"}
      </button>

      {/* Sign up link */}
      <div style={{ marginTop:20, textAlign:"center" }}>
  <span style={{ fontFamily:FONT, fontSize:10, color:C.meta }}>New to BerBul?{" "}</span>
  <Link to="/Signup" style={{ fontFamily:FONT, fontSize:10, color:C.cyan, textDecoration:"none", fontWeight:500 }}>
  Create account →
  </Link>
  </div>
  </div>

  {/* ── Demo credentials ────────────────────────────────────────────── */}
  <div style={{
    marginTop:    20,
        padding:      "14px 20px",
        background:   C.bg1,
        border:       `1px solid ${C.border}`,
        borderRadius: 8,
        display:      "flex",
        justifyContent: "space-between",
        alignItems:   "center",
  }}>
  <span style={{ fontFamily:FONT, fontSize:9, color:C.meta, letterSpacing:".5px" }}>
  DEMO ACCOUNT
  </span>
  <span style={{ fontFamily:FONT, fontSize:10, color:C.body }}>
  demo&nbsp;/&nbsp;demo123
  </span>
  </div>

  </div>
  </div>
  );
};

export default Login;